import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ShieldCheck, Lock } from "lucide-react";

const F = "bg-slate-800 border-white/10 text-white";
const MODES = [{ v: "test", l: "Razorpay TEST" }, { v: "live", l: "LIVE" }];
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/razorpay`;

function ConnectionCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/payment-settings/test-connection"); setRes(data); }
    catch (e) { setRes({ ok: false, error: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-xs">
      <div className="text-slate-400">Webhook URL (Razorpay Dashboard → Webhooks)</div>
      <code className="block text-teal-300 break-all" data-testid="pay-webhook-url">{WEBHOOK_URL}</code>
      <div className="text-slate-500">Events: payment.captured, order.paid, payment.failed, refund.processed</div>
      <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="pay-test-connection">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Test gateway connection</Button>
      {res && <div data-testid="pay-connection-result" className={res.ok ? "text-teal-400" : "text-red-400"}>{res.ok ? `Connected to Razorpay ${res.mode?.toUpperCase()}` : res.error}</div>}
    </div>
  );
}

function Gateway() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [creds, setCreds] = useState({ mode: "test", key_id: "", key_secret: "", webhook_secret: "" });
  const load = () => api.get("/admin/payment-settings").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const saveConfig = async (patch) => {
    try { const { data } = await api.put("/admin/payment-settings", { ...patch }); setS(data); toast.success("Payment settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const saveCreds = async () => {
    if (!creds.key_id || !creds.key_secret) return toast.error("Key ID and Secret required");
    try { const { data } = await api.post("/admin/payment-settings/credentials", creds); toast.success(`${creds.mode.toUpperCase()} credentials stored (${data.key_id})`); setCreds({ ...creds, key_id: "", key_secret: "", webhook_secret: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const st = s.credential_status || {};
  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div><div className="text-white font-semibold text-sm">Razorpay</div>
            <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-teal-400" /> {s.mode === "live" ? "LIVE" : "Razorpay TEST"} · {s.enabled ? "Enabled" : "Disabled"}</div></div>
          <Switch checked={!!s.enabled} onCheckedChange={(v) => saveConfig({ enabled: v })} data-testid="pay-enabled" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Mode</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {MODES.map((m) => (
              <button key={m.v} data-testid={`pay-mode-${m.v}`}
                onClick={() => { if (m.v === "live") { if (!window.confirm("Enable LIVE payments? Real money will be charged.")) return; saveConfig({ mode: "live", confirm_live: true }); } else saveConfig({ mode: m.v }); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${s.mode === m.v ? "bg-teal text-white border-teal" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{m.l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-800/50 rounded-lg p-3"><div className="text-slate-400">TEST credentials</div><div className={`font-semibold ${s.test_configured ? "text-teal-400" : "text-slate-500"}`}>{s.test_configured ? `Configured · ${st.test?.key_id}` : "Not configured"}</div></div>
          <div className="bg-slate-800/50 rounded-lg p-3"><div className="text-slate-400">LIVE credentials</div><div className={`font-semibold ${s.live_configured ? "text-amber-400" : "text-slate-500"}`}>{s.live_configured ? `Configured · ${st.live?.key_id}` : "Not configured"}</div></div>
        </div>
        <ConnectionCheck />
      </div>
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-3">
        <div className="text-white font-semibold text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-teal-400" /> {user?.role === "super_admin" ? "Add / Replace credentials" : "Credential management (Super Admin only)"}</div>
        {user?.role === "super_admin" ? (
          <>
            <p className="text-xs text-slate-500">Secrets are stored securely and never displayed again. Replacing overwrites the stored value.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <select className={F + " rounded-md px-3 text-sm"} value={creds.mode} onChange={(e) => setCreds({ ...creds, mode: e.target.value })} data-testid="cred-mode"><option value="test">TEST</option><option value="live">LIVE</option></select>
              <Input className={F} placeholder="Key ID (rzp_...)" value={creds.key_id} onChange={(e) => setCreds({ ...creds, key_id: e.target.value })} data-testid="cred-keyid" />
              <Input className={F} type="password" placeholder="Key Secret" value={creds.key_secret} onChange={(e) => setCreds({ ...creds, key_secret: e.target.value })} data-testid="cred-secret" />
              <Input className={F} type="password" placeholder="Webhook Secret (optional)" value={creds.webhook_secret} onChange={(e) => setCreds({ ...creds, webhook_secret: e.target.value })} data-testid="cred-webhook" />
            </div>
            <Button className="bg-teal hover:bg-teal-dark text-white" onClick={saveCreds} data-testid="cred-save">Store credentials securely</Button>
          </>
        ) : <p className="text-xs text-slate-500">Only a Super Admin can add or replace payment secrets.</p>}
      </div>
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div><div className="text-white font-semibold text-sm">International payments</div>
          <div className="text-xs text-slate-500 mt-0.5">Website preference only — actual eligibility depends on your Razorpay merchant activation / KYC.</div></div>
          <Switch checked={!!s.international_enabled} onCheckedChange={(v) => saveConfig({ international_enabled: v })} data-testid="pay-intl" />
        </div>
        <div className="border-t border-white/5 pt-3">
          <div className="text-white font-semibold text-sm mb-2">Payment method preferences</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(s.payment_methods || {}).map(([m, on]) => (
              <label key={m} className="flex items-center gap-2 text-xs text-slate-300 capitalize">
                <input type="checkbox" checked={!!on} onChange={(e) => saveConfig({ payment_methods: { ...(s.payment_methods || {}), [m]: e.target.checked } })} data-testid={`pay-method-${m}`} /> {m}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">Final availability is controlled by Razorpay for your account; unavailable methods are hidden gracefully at checkout.</p>
        </div>
      </div>
    </div>
  );
}

function Transactions() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["payments", status], queryFn: async () => (await api.get(`/admin/payments${status ? `?status=${status}` : ""}`)).data });
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const rows = data.filter((r) => !q || (r.order_no || "").toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input className={F + " max-w-xs"} placeholder="Search order no / email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pay-search" />
        <select className={F + " rounded-md px-3 text-sm"} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pay-status-filter">
          {["", "PAID", "CREATED", "FAILED", "REFUNDED"].map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-3">Order</th><th className="text-left px-4 py-3">Customer</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Payment</th><th className="text-left px-4 py-3">Ref</th><th className="text-left px-4 py-3">Invoice</th></tr></thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.order_id} data-testid={`pay-row-${r.order_no}`}>
                <td className="px-4 py-3 font-mono text-xs text-white">{r.order_no}</td>
                <td className="px-4 py-3 text-slate-300">{r.email}</td>
                <td className="px-4 py-3 text-right text-slate-200">{inr(r.amount)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${r.payment_status === "PAID" ? "bg-teal/15 text-teal-300" : r.payment_status === "REFUNDED" ? "bg-amber-500/15 text-amber-300" : "bg-slate-700 text-slate-300"}`}>{r.payment_status}</span></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{r.payment_ref || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{r.invoice_no || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-4 text-slate-500 text-sm">No transactions.</p>}
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const { can } = useAuth();
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Payments</h1>
      <Tabs defaultValue={can("payments.manage") ? "gateway" : "transactions"}>
        <TabsList className="bg-slate-800">
          {can("payments.manage") && <TabsTrigger value="gateway" data-testid="pay-tab-gateway">Gateway Settings</TabsTrigger>}
          <TabsTrigger value="transactions" data-testid="pay-tab-tx">Transactions</TabsTrigger>
        </TabsList>
        {can("payments.manage") && <TabsContent value="gateway" className="mt-4"><Gateway /></TabsContent>}
        <TabsContent value="transactions" className="mt-4"><Transactions /></TabsContent>
      </Tabs>
    </div>
  );
}
